import { FC, PropsWithChildren, ReactElement } from 'react'
import { withRouter, useRouter } from 'next/router'
import { WithRouterProps } from 'next/dist/client/with-router'
import useLogin from 'hooks/useLogin'
import useNotification from 'hooks/useNotification'
import Typography from 'components/Typography'
import Banner from 'components/Banner'
import Tabs from 'components/navigation/Tabs'
import { User } from 'graphql/types'
import logger from 'lib/logger'
import Head, { TProps as THeadProps } from '../components/Head'
import Header, { THeaderSharedProps } from '../components/Header'
import { menuNavigation } from '../constants'
import styles from './Hub.module.scss'

export type TProps = {
  headerProps?: THeaderSharedProps
  noLegalNotification?: boolean
  head: THeadProps
  checkUserType?: boolean
}

interface TContentProps extends WithRouterProps {
  userProfile: User
  children: ReactElement
}

const RoleContent = withRouter(
  ({ router, userProfile, children }: TContentProps) => {
    const { role, type } = userProfile ?? {}

    if (
      role === 'admin' ||
      type === 'partner_broker' ||
      type === 'partner_referrer'
    ) {
      return children
    }

    return (
      <div className={styles.ecRoleContent}>
        <div className={styles.ecBannersWrapper}>
          <Typography
            bold
            variant="Title"
            className={styles.ecRoleContentTitle}
          >
            Para indicar imóvel, <strong>inscreva-se</strong> no programa que é
            mais adequado para você.
          </Typography>
          <div className={styles.ecRoleContentBanners}>
            <Banner
              button={{
                label: 'Iniciar cadastro',
                props: {
                  'aria-label': 'Iniciar cadastro',
                  onClick: () => {
                    logger.action('hub-partner-register-click', {
                      isPartner: true,
                    })
                    void router.push('/corretor-parceiro/cadastrar')
                  },
                },
              }}
              onlyMobileLayout
              noPadding
            >
              <Typography variant="Title" bold>
                Eu sou corretor
              </Typography>
              <Typography>
                Se você tiver <strong>CRECI</strong>, faça seu cadastro, que
                trabalharemos seu imóvel para você, quando o imóvel indicado for
                vendido você ganha <strong>parte da comissão da EmCasa</strong>
              </Typography>
            </Banner>
          </div>
        </div>
      </div>
    )
  },
)

const tabsItems = [
  {
    label: 'Meus dados',
    activeKey: '/perfil/dados',
    href: '/perfil/dados',
  },
  {
    label: 'Visitas',
    activeKey: '/interesses/visitas',
    href: '/interesses/visitas',
  },
  {
    label: 'Propostas',
    activeKey: '/interesses/propostas',
    href: '/interesses/propostas',
  },
  {
    label: 'Minhas indicações',
    activeKey: '/perfil/minhas-indicacoes',
    href: '/perfil/minhas-indicacoes',
  },
  {
    label: 'Meus imóveis',
    activeKey: '/meus-imoveis',
    href: '/meus-imoveis',
  },
]

const Hub: FC<PropsWithChildren<TProps>> = ({
  children,
  noLegalNotification,
  head,
  headerProps,
  checkUserType,
}): JSX.Element => {
  const router = useRouter()
  const { isVisible, Notification } = useNotification({
    noLegalNotification,
  })
  const { isAuthenticated, Login, userProfile } = useLogin()

  return (
    <div className={styles.ecDefault}>
      <Head {...head} />
      {!headerProps?.hide && <Header nav={menuNavigation} {...headerProps} />}
      <main className={styles.ecMain}>
        <div className={styles.ecTabWrapper}>
          <Tabs activeKey={router.pathname} items={tabsItems} />
        </div>
        {isAuthenticated && checkUserType && userProfile && (
          <RoleContent userProfile={userProfile}>
            {children as ReactElement}
          </RoleContent>
        )}
        {isAuthenticated && !(checkUserType && userProfile) && children}
        {!isAuthenticated && (
          <div className={styles.ecNotAuthenticated}>
            <Login />
          </div>
        )}
      </main>
      {isVisible && <Notification />}
    </div>
  )
}

export default Hub
